import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./AdminPage.css";
import { PLAN_OPTIONS } from "../domainCatalog";


const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const AdminPage = ({ token }) => {
  const [users, setUsers] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [workspacePlans, setWorkspacePlans] = useState({});
  const [workspaceOwners, setWorkspaceOwners] = useState({});
  const [savingWorkspaceId, setSavingWorkspaceId] = useState(null);
  const [message, setMessage] = useState("");

  const authHeaders = useMemo(() => ({
    Authorization: `Bearer ${token}`
  }), [token]);

  const loadAdminData = useCallback(async () => {
    try {
      const [usersRes, workspacesRes] = await Promise.all([
        fetch(`${API_BASE_URL}/admin/users`, { headers: authHeaders }),
        fetch(`${API_BASE_URL}/admin/workspaces`, { headers: authHeaders })
      ]);
      const [usersData, workspacesData] = await Promise.all([
        usersRes.json(),
        workspacesRes.json()
      ]);
      if (!usersRes.ok) throw new Error(usersData.detail || "Failed to load users.");
      if (!workspacesRes.ok) throw new Error(workspacesData.detail || "Failed to load accounts.");

      setUsers(usersData);
      setWorkspaces(workspacesData);
      setWorkspacePlans(Object.fromEntries(
        workspacesData.map((workspace) => [workspace.id, workspace.plan_name])
      ));
      setWorkspaceOwners(Object.fromEntries(
        workspacesData.map((workspace) => [workspace.id, workspace.owner_user_id])
      ));
    } catch (error) {
      setMessage(error.message || "Error loading admin data.");
    }
  }, [authHeaders]);

  useEffect(() => {
    loadAdminData();
  }, [loadAdminData]);

  const updateWorkspace = (updatedWorkspace) => {
    setWorkspaces((current) => current.map((workspace) => (
      workspace.id === updatedWorkspace.id ? updatedWorkspace : workspace
    )));
    setWorkspacePlans((current) => ({
      ...current,
      [updatedWorkspace.id]: updatedWorkspace.plan_name
    }));
    setWorkspaceOwners((current) => ({
      ...current,
      [updatedWorkspace.id]: updatedWorkspace.owner_user_id
    }));
  };

  const saveWorkspacePlan = async (workspaceId) => {
    const planName = workspacePlans[workspaceId];
    if (!PLAN_OPTIONS.some((plan) => plan.id === planName)) {
      setMessage("Select a valid plan.");
      return;
    }

    setSavingWorkspaceId(workspaceId);
    setMessage("");
    try {
      const res = await fetch(`${API_BASE_URL}/admin/workspaces/${workspaceId}/plan`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders
        },
        body: JSON.stringify({ plan_name: planName })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to update subscription.");

      updateWorkspace(data);
      setMessage(`Subscription updated for ${data.name}.`);
    } catch (error) {
      setMessage(error.message || "Error updating subscription.");
    } finally {
      setSavingWorkspaceId(null);
    }
  };

  const saveWorkspaceOwner = async (workspaceId) => {
    const ownerUserId = Number(workspaceOwners[workspaceId]);
    if (!ownerUserId) {
      setMessage("Select a valid owner.");
      return;
    }

    setSavingWorkspaceId(workspaceId);
    setMessage("");
    try {
      const res = await fetch(`${API_BASE_URL}/admin/workspaces/${workspaceId}/owner`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders
        },
        body: JSON.stringify({ owner_user_id: ownerUserId })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to update owner.");

      updateWorkspace(data);
      setMessage(`Owner updated for ${data.name}.`);
    } catch (error) {
      setMessage(error.message || "Error updating owner.");
    } finally {
      setSavingWorkspaceId(null);
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <h1>Admin</h1>
          <p>Manage accounts, owners, subscriptions, and current-month AI usage.</p>
        </div>
        <button onClick={loadAdminData}>Refresh</button>
      </div>

      {message && <div className="admin-message">{message}</div>}

      <section className="admin-section">
        <div className="admin-section-header">
          <h2>Accounts</h2>
          <p>Each account is a workspace. Owners can invite people and manage settings.</p>
        </div>
        <div className="admin-table-shell">
          <table className="admin-table admin-account-table">
            <thead>
              <tr>
                <th>Account</th>
                <th>Owner</th>
                <th>Plan</th>
                <th>Projects</th>
                <th>People</th>
                <th>Members</th>
                <th>AI tokens</th>
              </tr>
            </thead>
            <tbody>
              {workspaces.map((workspace) => (
                <tr key={workspace.id}>
                  <td>
                    <strong>{workspace.name}</strong>
                    <span className="admin-muted">ID {workspace.id}</span>
                  </td>
                  <td>
                    <div className="admin-inline-actions">
                      <select
                        aria-label={`Owner for ${workspace.name}`}
                        value={workspaceOwners[workspace.id] ?? workspace.owner_user_id}
                        onChange={(event) => setWorkspaceOwners((current) => ({
                          ...current,
                          [workspace.id]: event.target.value
                        }))}
                      >
                        {workspace.members.map((member) => (
                          <option key={member.user_id} value={member.user_id}>
                            {member.email}
                          </option>
                        ))}
                      </select>
                      <button
                        className="primary"
                        onClick={() => saveWorkspaceOwner(workspace.id)}
                        disabled={savingWorkspaceId === workspace.id}
                      >
                        Save owner
                      </button>
                    </div>
                  </td>
                  <td>
                    <div className="admin-inline-actions">
                      <select
                        aria-label={`Plan for ${workspace.name}`}
                        value={workspacePlans[workspace.id] ?? workspace.plan_name}
                        onChange={(event) => setWorkspacePlans((current) => ({
                          ...current,
                          [workspace.id]: event.target.value
                        }))}
                      >
                        {PLAN_OPTIONS.map((plan) => (
                          <option key={plan.id} value={plan.id}>{plan.label}</option>
                        ))}
                      </select>
                      <button
                        className="primary"
                        onClick={() => saveWorkspacePlan(workspace.id)}
                        disabled={savingWorkspaceId === workspace.id}
                      >
                        Save plan
                      </button>
                    </div>
                  </td>
                  <td>{workspace.project_count} / {workspace.project_limit}</td>
                  <td>{workspace.member_count} / {workspace.member_limit}</td>
                  <td>
                    <div className="admin-member-list">
                      {workspace.members.map((member) => (
                        <span key={member.id} className="admin-member-pill">
                          {member.email} ({member.role})
                        </span>
                      ))}
                    </div>
                  </td>
                  <td>{workspace.monthly_tokens_used.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="admin-section">
        <div className="admin-section-header">
          <h2>Users</h2>
          <p>Raw registered users for quick lookup.</p>
        </div>
        <div className="admin-table-shell">
          <table className="admin-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Plan</th>
                <th>Projects</th>
                <th>People</th>
                <th>AI tokens used this month</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>{user.email}</td>
                  <td>{user.plan_label}</td>
                  <td>{user.project_count} / {user.project_limit}</td>
                  <td>{user.member_count} / {user.member_limit}</td>
                  <td>{user.monthly_tokens_used.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default AdminPage;
